/**
 * Workout-level rollups: average + peak HR over the whole workout, the lap
 * indices for peak HR and fastest duration, and the stroke breakdown (meters
 * per stroke style + percentage).
 */
import type { FirehoseTypeBucket } from "@contract/types";
import type { Lap } from "./laps";
import { averageOf, maxOf, peakSampleOf } from "./_reduce";

export interface StrokeBreakdownEntry {
  style: string;
  lengths: number;
  meters: number;
  pctMeters: number;
}

export interface WorkoutAggregates {
  avgHr: number | null;
  maxHr: number | null;
  peakHrTs: number | null;
  peakLapIndex: number | null;     // lap with highest avg HR
  fastestLapIndex: number | null;  // lap with shortest duration > 0
  strokeBreakdown: StrokeBreakdownEntry[];
}

export function computeWorkoutAggregates(
  laps: Lap[],
  poolLengthMeters: number | null,
  hrBucket: FirehoseTypeBucket | undefined,
): WorkoutAggregates {
  const avgHr = hrBucket ? averageOf(hrBucket.samples) : null;
  const maxHr = hrBucket ? maxOf(hrBucket.samples) : null;
  const peakHrSample = hrBucket ? peakSampleOf(hrBucket.samples) : null;
  const peakHrTs = peakHrSample ? (() => { const ts = new Date(peakHrSample.startDate).getTime(); return Number.isFinite(ts) ? ts : null; })() : null;

  // DRILL-03: every per-lap rollup below iterates the filtered set.
  const includedLaps = laps.filter((l) => !l.effectiveExcluded);

  let peakLapIndex: number | null = null;
  let peakHr = -Infinity;
  for (const lap of includedLaps) {
    if (lap.avgHr !== null && lap.avgHr > peakHr) {
      peakHr = lap.avgHr;
      peakLapIndex = lap.index;
    }
  }

  let fastestLapIndex: number | null = null;
  let fastestSec = Infinity;
  for (const lap of includedLaps) {
    if (lap.durationSeconds > 0 && lap.durationSeconds < fastestSec) {
      fastestSec = lap.durationSeconds;
      fastestLapIndex = lap.index;
    }
  }

  const breakdownMap = new Map<string, { lengths: number; meters: number }>();
  for (const lap of includedLaps) {
    const meters = lap.lengthMeters ?? poolLengthMeters ?? 0;
    const cur = breakdownMap.get(lap.strokeStyle) ?? { lengths: 0, meters: 0 };
    cur.lengths += 1;
    cur.meters += meters;
    breakdownMap.set(lap.strokeStyle, cur);
  }
  const totalMeters = includedLaps.reduce((sum, l) => sum + (l.lengthMeters ?? poolLengthMeters ?? 0), 0);
  const strokeBreakdown = Array.from(breakdownMap.entries())
    .map(([style, { lengths, meters }]) => ({
      style,
      lengths,
      meters,
      pctMeters: totalMeters > 0 ? (meters / totalMeters) * 100 : 0,
    }))
    .sort((a, b) => b.meters - a.meters);

  return { avgHr, maxHr, peakHrTs, peakLapIndex, fastestLapIndex, strokeBreakdown };
}
